import React, { useState, useRef, useEffect } from 'react';
import { Icons } from './Icons';
import { useTree } from '../contexts/TreeContext';
import { auth } from '../utils/firebase';
import { track } from '../utils/analytics';

const INPUT = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 13, color: '#2C2418', outline: 'none',
  fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
};

const LABEL = {
  fontSize: 11, fontWeight: 700, color: '#6B5E4F',
  display: 'block', marginBottom: 5,
  textTransform: 'uppercase', letterSpacing: 0.8,
};

const EMPTY = (gender = 'M') => ({ firstName: '', lastName: '', birth: '', gender });

function PersonForm({ data, onChange, firstRef }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div>
        <label style={LABEL}>First Name *</label>
        <input ref={firstRef} value={data.firstName}
          onChange={e => onChange({ ...data, firstName: e.target.value })}
          placeholder="e.g. John" style={INPUT} />
      </div>
      <div>
        <label style={LABEL}>Last Name</label>
        <input value={data.lastName}
          onChange={e => onChange({ ...data, lastName: e.target.value })}
          placeholder="e.g. Smith" style={INPUT} />
      </div>
      <div>
        <label style={LABEL}>Date of Birth</label>
        <input value={data.birth}
          onChange={e => onChange({ ...data, birth: e.target.value })}
          placeholder="YYYY-MM-DD" style={INPUT} />
      </div>
      <div>
        <label style={LABEL}>Gender</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['M', 'Male'], ['F', 'Female'], ['O', 'Other']].map(([v, l]) => (
            <button key={v} onClick={() => onChange({ ...data, gender: v })} style={{
              flex: 1, padding: '8px 0', borderRadius: 8, fontFamily: "'DM Sans', sans-serif",
              border: data.gender === v ? '2px solid #C4975A' : '1.5px solid #D5CAB8',
              background: data.gender === v ? '#F5EDE3' : '#fff',
              color: data.gender === v ? '#C4975A' : '#6B5E4F',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{
      margin: '0 0 12px', fontSize: 11, fontWeight: 700, color: '#C4975A',
      textTransform: 'uppercase', letterSpacing: 1,
    }}>{children}</p>
  );
}

function Divider() {
  return <div style={{ borderTop: '1px solid #EBE4D8', margin: '20px 0' }} />;
}

function ProgressDots({ step }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '4px 0 20px' }}>
      {[1, 2, 3].map(n => (
        <div key={n} style={{
          width: n === step ? 22 : 8, height: 8, borderRadius: 4, transition: 'width 0.2s',
          background: n < step ? '#3B2F1E' : n === step ? '#C4975A' : '#D5CAB8',
        }} />
      ))}
    </div>
  );
}

const STEP_META = {
  1: { title: 'Start with yourself',       subtitle: "You're the root of this tree." },
  2: { title: 'Add your parents',          subtitle: 'Optional — leave blank if unknown.' },
  3: { title: 'Add your children',         subtitle: 'Optional — you can add more later.' },
};

export default function TreeWizard({ onClose }) {
  const { addPerson, updatePerson } = useTree();

  const [step, setStep]   = useState(1);
  const [saving, setSaving]      = useState(false);
  const [saveError, setSaveError] = useState('');
  const [selfId, setSelfId]      = useState(null);

  const [self,    setSelf]    = useState(EMPTY('M'));
  const [parent1, setParent1] = useState(EMPTY('M'));
  const [parent2, setParent2] = useState(EMPTY('F'));
  const [children, setChildren] = useState([EMPTY('M')]);

  const firstRef = useRef(null);
  useEffect(() => { firstRef.current?.focus(); }, []);

  // Re-focus when step changes
  useEffect(() => { firstRef.current?.focus(); }, [step]);

  const buildPersonData = ({ firstName, lastName, birth, gender }, extras = {}) => ({
    name: [firstName.trim(), lastName.trim()].filter(Boolean).join(' '),
    birth: birth || null,
    death: null, birthPlace: null, deathPlace: null, occupation: null,
    gender, parentIds: [], marriages: [], children: [], photoURL: null,
    ...extras,
  });

  // Every step writes, and a write can be refused — by a quota, by a role, by an
  // unverified email. Without this the rejection went nowhere: `saving` stayed
  // true because the setter after the await never ran, so the wizard sat on
  // "Saving…" for good and the reason was only ever in the console. The button
  // has to come back and the message has to be on screen.
  const guard = async (fn) => {
    setSaving(true);
    setSaveError('');
    try {
      await fn();
    } catch (e) {
      setSaveError(e?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Step 1: save self ──────────────────────────────────────────────────────
  const handleStep1Next = async () => {
    if (!self.firstName.trim()) return;
    await guard(async () => {
      // Ensure the JWT is current — email_verified can be stale in the token
      // if the user verified their email in this session without a token refresh.
      if (auth.currentUser) await auth.currentUser.getIdToken(true);
      const id = await addPerson(buildPersonData(self));
      setSelfId(id);
      setStep(2);
    });
  };

  // ── Step 2: save any parents that have a name ──────────────────────────────
  const handleStep2Next = async () => {
    await guard(async () => {
      const parentIds = [];
      for (const parent of [parent1, parent2]) {
        if (parent.firstName.trim()) {
          const pid = await addPerson(buildPersonData(parent));
          await updatePerson(pid, { children: [selfId] });
          parentIds.push(pid);
        }
      }
      if (parentIds.length > 0) {
        await updatePerson(selfId, { parentIds });
        // The wizard links as it creates, so a link here is a relationship the
        // same as one made from the tree later. Counting only the explicit ones
        // would make the first minutes of an account look like nobody connects
        // anyone — which is precisely the step the wizard exists to get past.
        parentIds.forEach(() => track('relationship_added', { type: 'parent', via: 'wizard' }));
      }
      setStep(3);
    });
  };

  // ── Step 3: save any children that have a name, then close ────────────────
  const handleFinish = async () => {
    await guard(async () => {
      const childIds = [];
      for (const child of children) {
        if (child.firstName.trim()) {
          const childId = await addPerson(buildPersonData(child, { parentIds: [selfId] }));
          childIds.push(childId);
        }
      }
      if (childIds.length > 0) {
        await updatePerson(selfId, { children: childIds });
        childIds.forEach(() => track('relationship_added', { type: 'child', via: 'wizard' }));
      }
      onClose();
    });
  };

  const addChildSlot = () => setChildren(prev => [...prev, EMPTY('M')]);
  const updateChild  = (i, data) => setChildren(prev => prev.map((c, idx) => idx === i ? data : c));

  const meta = STEP_META[step];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.55)', zIndex: 150,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: 540, maxHeight: '90vh', background: '#FDFBF7', borderRadius: 18,
        boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          padding: '22px 26px 18px', borderBottom: '1px solid #E8E0D4',
          background: '#F5EDE3', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ margin: '0 0 2px', fontSize: 11, fontWeight: 700, color: '#C4975A', textTransform: 'uppercase', letterSpacing: 1 }}>
                Step {step} of 3
              </p>
              <h2 style={{ margin: 0, fontSize: 20, fontFamily: "'Crimson Pro', Georgia, serif", color: '#2C2418', fontWeight: 700 }}>
                {meta.title}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8B7D6B' }}>
                {meta.subtitle}
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B', padding: 4, marginTop: -2 }}>
              <Icons.Close />
            </button>
          </div>
          <ProgressDots step={step} />
        </div>

        {/* Body */}
        <div style={{ padding: '22px 26px', overflowY: 'auto', flex: 1 }}>

          {/* ── Step 1: you ── */}
          {step === 1 && (
            <PersonForm data={self} onChange={setSelf} firstRef={firstRef} />
          )}

          {/* ── Step 2: parents ── */}
          {step === 2 && (
            <>
              <SectionLabel>Parent 1</SectionLabel>
              <PersonForm data={parent1} onChange={setParent1} firstRef={firstRef} />
              <Divider />
              <SectionLabel>Parent 2</SectionLabel>
              <PersonForm data={parent2} onChange={setParent2} />
            </>
          )}

          {/* ── Step 3: children ── */}
          {step === 3 && (
            <>
              {children.map((child, i) => (
                <div key={i}>
                  {children.length > 1 && <SectionLabel>Child {i + 1}</SectionLabel>}
                  <PersonForm
                    data={child}
                    onChange={data => updateChild(i, data)}
                    firstRef={i === 0 ? firstRef : undefined}
                  />
                  {i < children.length - 1 && <Divider />}
                </div>
              ))}
              <button onClick={addChildSlot} style={{
                marginTop: 16, background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: '#C4975A', padding: 0,
                fontFamily: "'DM Sans', sans-serif",
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <Icons.Plus size={11} /> Add another child
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 26px', borderTop: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          gap: 10, flexShrink: 0,
        }}>
          {saveError && (
            <p style={{
              margin: 0, marginRight: 'auto', maxWidth: 380,
              fontSize: 12, color: '#C0574A', lineHeight: 1.45,
            }}>
              {saveError}
            </p>
          )}

          {step === 1 && (
            <button
              onClick={handleStep1Next}
              disabled={saving || !self.firstName.trim()}
              style={primaryBtn(saving || !self.firstName.trim())}
            >
              {saving ? 'Saving…' : 'Next →'}
            </button>
          )}

          {step === 2 && (
            <>
              <button onClick={handleStep2Next} disabled={saving} style={ghostBtn}>
                {saving ? 'Saving…' : 'Skip'}
              </button>
              <button onClick={handleStep2Next} disabled={saving} style={primaryBtn(saving)}>
                {saving ? 'Saving…' : 'Next →'}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <button onClick={onClose} disabled={saving} style={ghostBtn}>
                Skip
              </button>
              <button onClick={handleFinish} disabled={saving} style={primaryBtn(saving)}>
                {saving ? 'Saving…' : 'Finish'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const primaryBtn = (disabled) => ({
  padding: '10px 28px', borderRadius: 8, border: 'none',
  background: disabled ? '#B8AFA0' : '#3B2F1E',
  color: '#F5EDE3', fontSize: 13, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontFamily: "'DM Sans', sans-serif",
});

const ghostBtn = {
  padding: '10px 20px', borderRadius: 8, border: '1.5px solid #D5CAB8',
  background: '#fff', color: '#6B5E4F', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
};
